import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { formatDayLabel } from '@/lib/dates'
import { useCreateRequestLinearIssue } from '@/queries/clientMeetings'
import type { CallRequest, RequestSource } from '@/types/clientMeetings'

function draftDescription(request: CallRequest, source: RequestSource, companyName: string) {
  const place = source.kind === 'email' ? `in a ${companyName} email` : `on the ${companyName} call`
  const where = `Raised by ${request.raisedBy ?? 'the client'} ${place} "${source.title}", ${formatDayLabel(source.date)}`
  return `${request.detail}\n\n${where}${request.url ? `: ${request.url}` : ''}`
}

// Raise a Linear issue on the client's Linear customer for internal work the client shouldn't see on their tracker.
export function CreateRequestLinearDialog({
  request,
  source,
  companyName,
  open,
  onOpenChange,
}: {
  request: CallRequest
  source: RequestSource
  companyName: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [title, setTitle] = useState(request.title)
  const [description, setDescription] = useState(() => draftDescription(request, source, companyName))
  const create = useCreateRequestLinearIssue()
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Create Linear issue</DialogTitle>
          <DialogDescription>Goes to Linear triage on {companyName}'s customer. The client doesn't see it on their tracker.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="request-linear-title">Title</Label>
            <Input id="request-linear-title" value={title} onChange={event => setTitle(event.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="request-linear-description">Description</Label>
            <Textarea id="request-linear-description" rows={7} value={description} onChange={event => setDescription(event.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!title.trim() || create.isPending}
            onClick={() => create.mutate({ requestId: request._id, title: title.trim(), description }, { onSuccess: () => onOpenChange(false) })}
          >
            Create Linear issue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
